import { useEffect, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  ArrowLeft,
  Download,
  Eye,
  Loader2,
  Presentation,
} from "lucide-react";
import { base_url } from "../../config";
import axios from "axios";
import {
  darkBodyCellSx,
  darkHeadCellSx,
  darkPaperSx,
  darkRowSx,
  darkSearchSx,
  darkToolbarSx,
} from "../Codes/darkTable";

const Workshops = ({ ws }) => {
  const [global, showGlobal] = useState(true);
  const [wsShown, setWsShown] = useState(null);
  const [wsParticipants, setWsParticipants] = useState([]);
  const [loading, setLoading] = useState(false);

  const selectWorkshop = (row) => {
    setWsShown(row.id);
    showGlobal(false);
  };

  const goBack = () => {
    setWsShown(null);
    setWsParticipants([]);
    showGlobal(true);
  };

  const handleDownload = async (ws_code, which) => {
    try {
      const response = await axios.post(
        `${base_url}/api/admin/export_ws_participants/${ws_code}/${which}`,
        { title: `Workshop attendees ${ws_code}` },
        { responseType: "blob" }
      );
      const pdfBlob = new Blob([response.data], { type: "application/xlsx" });
      const url = window.URL.createObjectURL(pdfBlob);
      const tempLink = document.createElement("a");
      tempLink.href = url;
      tempLink.setAttribute(
        "download",
        `Workshop attendees ${ws_code}.xlsx`
      );
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading report: ", error);
      alert("Failed to download report. Please try again later.");
    }
  };

  useEffect(() => {
    const fetchParticipants = async () => {
      if (wsShown) {
        setLoading(true);
        try {
          const response = await axios.get(
            `${base_url}/api/admin/get_ws_participants/${wsShown}`
          );
          setWsParticipants(response.data);
        } catch (error) {
          console.error("Error fetching participants:", error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchParticipants();
  }, [wsShown]);

  const columnsGlobal = [
    {
      accessorKey: "workshop_code",
      header: "Code",
      size: 90,
      maxSize: 100,
      Cell: ({ cell }) => (
        <span className="font-mono text-[11px] px-2 py-0.5 rounded-md bg-slate-700/40 text-slate-100 border border-slate-600/40">
          {cell.getValue()}
        </span>
      ),
    },
    {
      id: "title",
      accessorKey: "title",
      header: "Title",
      size: 500,
      minSize: 400,
      Cell: ({ cell }) => (
        <span className="text-slate-100">{cell.getValue()}</span>
      ),
    },
    {
      id: "attendees",
      accessorKey: "attendees",
      header: "Attendees",
      size: 90,
      Cell: ({ cell }) => (
        <div className="text-center font-bold text-emerald-300">
          {cell.getValue()}
        </div>
      ),
    },
    {
      id: "attendees_in_progress",
      accessorKey: "attendees_in_progress",
      header: "In progress",
      size: 100,
      Cell: ({ cell }) => (
        <div className="text-center text-amber-300">{cell.getValue()}</div>
      ),
    },
    {
      id: "actions",
      accessorKey: "actions",
      header: "",
      enableSorting: false,
      enableColumnActions: false,
      size: 220,
      Cell: ({ cell }) => {
        const row = cell.row.original;
        return (
          <div className="flex flex-row gap-1.5 justify-end">
            <button
              onClick={() => selectWorkshop(row)}
              title="View attendees"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-500/15 text-blue-200 border border-blue-500/30 hover:bg-blue-500/25 transition-colors"
            >
              <Eye className="h-3.5 w-3.5" />
              View
            </button>
            <button
              onClick={() => handleDownload(row.workshop_code, "all")}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-cyan-500/15 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              All
            </button>
            <button
              onClick={() => handleDownload(row.workshop_code, "consent")}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-slate-600/30 text-slate-200 border border-slate-600/40 hover:bg-slate-600/50 transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Consent
            </button>
          </div>
        );
      },
    },
  ];

  const columnsParticipants = [
    {
      id: "registration_id",
      accessorKey: "registration_id",
      header: "Reg. ID",
      size: 110,
      Cell: ({ cell }) => (
        <span className="font-mono text-[11px] px-2 py-0.5 rounded-md bg-slate-700/40 text-slate-100 border border-slate-600/40">
          {cell.getValue()}
        </span>
      ),
    },
    {
      accessorKey: "name",
      header: "Name",
      Cell: ({ cell }) => (
        <span className="text-slate-100">{cell.getValue()}</span>
      ),
    },
    {
      accessorKey: "email",
      header: "Email",
      Cell: ({ cell }) => (
        <span className="text-slate-400 text-xs">{cell.getValue()}</span>
      ),
    },
    {
      accessorKey: "max_step",
      header: "Status",
      size: 140,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        if (value === "Confirmation") {
          return (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              Completed
            </span>
          );
        }
        return (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium bg-amber-500/15 text-amber-300 border-amber-500/30">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
            In progress
          </span>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  const selectedWs = wsShown ? ws.find((i) => i.id === wsShown) : null;

  return (
    <div className="space-y-3">
      {global ? (
        <header className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
            <Presentation className="h-4 w-4 text-blue-400" />
            All workshops
          </h2>
          <span className="text-xs text-slate-400">{ws.length} total</span>
        </header>
      ) : (
        <header className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-sm font-semibold text-slate-100 flex items-center gap-2 min-w-0">
            <span className="font-mono text-[11px] px-2 py-0.5 rounded-md bg-slate-700/40 text-slate-100 border border-slate-600/40">
              {selectedWs?.workshop_code}
            </span>
            <span className="truncate">{selectedWs?.title}</span>
          </h2>
          <button
            onClick={goBack}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-slate-600/30 text-slate-200 border border-slate-600/40 hover:bg-slate-600/50 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to all workshops
          </button>
        </header>
      )}

      <div className="rounded-2xl overflow-hidden">
        <div style={{ display: global ? "block" : "none" }}>
          <MaterialReactTable
            data={ws}
            columns={columnsGlobal}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableHiding={false}
            initialState={{ density: "compact", pagination: { pageSize: 25, pageIndex: 0 } }}
            muiTablePaperProps={{ sx: darkPaperSx }}
            muiTableContainerProps={{ sx: { backgroundColor: "transparent" } }}
            muiTopToolbarProps={{ sx: darkToolbarSx }}
            muiBottomToolbarProps={{ sx: darkToolbarSx }}
            muiTableHeadCellProps={{ sx: darkHeadCellSx }}
            muiTableBodyCellProps={{ sx: darkBodyCellSx }}
            muiTableBodyRowProps={{ sx: darkRowSx }}
            muiSearchTextFieldProps={{ placeholder: "Search…", sx: darkSearchSx }}
          />
        </div>
        <div style={{ display: global ? "none" : "block" }}>
          <MaterialReactTable
            data={wsParticipants}
            columns={columnsParticipants}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableHiding={false}
            initialState={{ density: "compact", pagination: { pageSize: 25, pageIndex: 0 } }}
            muiTablePaperProps={{ sx: darkPaperSx }}
            muiTableContainerProps={{ sx: { backgroundColor: "transparent" } }}
            muiTopToolbarProps={{ sx: darkToolbarSx }}
            muiBottomToolbarProps={{ sx: darkToolbarSx }}
            muiTableHeadCellProps={{ sx: darkHeadCellSx }}
            muiTableBodyCellProps={{ sx: darkBodyCellSx }}
            muiTableBodyRowProps={{ sx: darkRowSx }}
            muiSearchTextFieldProps={{ placeholder: "Search…", sx: darkSearchSx }}
          />
        </div>
      </div>
    </div>
  );
};

export default Workshops;
